import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Crosshair } from "@/components/Crosshair";
import { toast } from "@/lib/toast";
import { Medal, RefreshCw, Lock, AlertTriangle, Copy, Download, Clock, ArrowRightLeft, Wrench, CheckCircle2 } from "lucide-react";

const pad3 = (n) => String(n).padStart(3, "0");

const STATUS_CLS = {
  reserved: "border-[#b08d2c]/50 text-[#b08d2c]/70 bg-[#b08d2c]/5",
  assigned: "border-[#7C8794] bg-[#5A6470] text-[#DDE2E7]",
  claimed: "border-[#4e8a1f] bg-[#4e8a1f] text-white",
  held: "border-[#C8A64E] bg-[#C8A64E]/15 text-[#E8C46A]",
  available: "border-sniper text-muted-foreground",
  retired: "border-blaze/60 text-blaze/70 bg-blaze/5 line-through",
};

const LEGEND = [
  ["reserved", "Reserved (1–10)"],
  ["assigned", "Assigned"],
  ["claimed", "Claimed"],
  ["held", "Held (temporary)"],
  ["available", "Available"],
  ["retired", "Retired"],
];

function money(cents) {
  if (cents == null) return "—";
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function ageDays(iso) {
  if (!iso) return null;
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return Number.isFinite(d) ? d : null;
}
function daysUntil(iso) {
  if (!iso) return null;
  const d = Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
  return Number.isFinite(d) ? d : null;
}

function SeatCell({ s }) {
  const cls = STATUS_CLS[s.status] || STATUS_CLS.available;
  const title = [
    `Seat #${pad3(s.seat_number)} · ${s.status}`,
    s.status === "held" && s.held_for_username ? `held for: @${s.held_for_username}` : (s.username ? `holder: @${s.username}` : null),
    s.status === "held" && s.hold_expires_at ? `hold expires ${new Date(s.hold_expires_at).toLocaleDateString()}` : null,
    s.status === "held" && s.held_by_username ? `held by @${s.held_by_username}` : null,
    s.account_type ? `type: ${s.account_type}` : null,
    s.claimed_at ? `since ${new Date(s.claimed_at).toLocaleDateString()}` : null,
    s.assigned_by_username ? `by @${s.assigned_by_username}` : null,
    s.reason ? `note: ${s.reason}` : null,
    s.collision ? "⚠ COLLISION — more than one document holds this number" : null,
  ].filter(Boolean).join("\n");
  return (
    <div
      data-testid={`seat-cell-${s.seat_number}`}
      title={title}
      className={`relative aspect-square flex flex-col items-center justify-center border text-[10px] font-mono ${cls}`}
    >
      {s.collision && <span className="absolute -top-1 -right-1 text-blaze" title="collision">⚠</span>}
      <span>{pad3(s.seat_number)}</span>
      {s.username && <span className="text-[8px] leading-none truncate max-w-full px-0.5 opacity-90">@{s.username}</span>}
    </div>
  );
}

// Build a plain-text, copy/paste-ready report of every collision.
function collisionsToText(collisions) {
  const lines = [];
  lines.push("OPTICSWAP — FOUNDING SEAT COLLISIONS");
  lines.push(`generated ${new Date().toISOString()}`);
  lines.push(`${collisions.length} colliding seat number(s). Tiebreak: first-to-pay. ADMIN DECIDES — never auto-resolved.`);
  lines.push("");
  for (const c of collisions) {
    lines.push(`SEAT #${pad3(c.seat_number)} — ${c.claims.length} claims`);
    for (const cl of c.claims) {
      const pay = cl.payment || {};
      lines.push(
        `  - @${cl.username || "?"} (${cl.account_type || "unknown type"}) ` +
        `| id_verified=${cl.identity_verified ? "yes" : "no"} | seat_status=${cl.seat_status}${cl.legacy_pool ? ` legacy_pool=${cl.legacy_pool}` : ""}` +
        `${cl.account_banned ? " [BANNED]" : ""}${cl.is_earliest_paid ? "  <<< FIRST TO PAY" : ""}`
      );
      lines.push(
        `      paid_at=${cl.paid_at || "—"} | amount=${money(pay.amount_cents)} ` +
        `| stripe_invoice=${pay.stripe_invoice_id || "—"} | stripe_sub=${pay.stripe_subscription_id || "—"}`
      );
      lines.push(`      seat_claimed_at=${cl.seat_claimed_at || "—"} | seat_doc_id=${cl.seat_doc_id} | user_id=${cl.user_id || "—"}`);
    }
    lines.push("");
  }
  return lines.join("\n");
}

function CollisionBanner({ collisions, isSuper, onRelease }) {
  const [copied, setCopied] = useState(false);
  const text = collisionsToText(collisions);

  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch { toast.error("Clipboard blocked — use Export instead."); }
  };
  const exportTxt = () => {
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `founding-collisions-${new Date().toISOString().slice(0, 10)}.txt`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="border border-blaze/70 bg-blaze/10 p-5 mb-6" data-testid="founding-collision-banner">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-blaze mt-0.5 shrink-0" />
          <div>
            <p className="font-heading uppercase tracking-tight text-blaze text-lg">
              {collisions.length} seat collision{collisions.length === 1 ? "" : "s"} — resolve before uniqueness is enforced
            </p>
            <p className="text-sm font-body text-secondary-foreground mt-1 max-w-[70ch]">
              Two documents hold the same number (legacy two-pool data). Nothing was auto-resolved and no one was renumbered.
              First-to-pay is the tiebreak, but you make the call. Release the losing claim to free the collision.
            </p>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={copy} data-testid="collision-copy" className="inline-flex items-center gap-1.5 border border-sniper hover:border-foreground px-3 py-1.5 font-mono text-[11px] uppercase text-foreground transition-colors">
            <Copy className="w-3.5 h-3.5" /> {copied ? "Copied" : "Copy report"}
          </button>
          <button onClick={exportTxt} data-testid="collision-export" className="inline-flex items-center gap-1.5 border border-sniper hover:border-foreground px-3 py-1.5 font-mono text-[11px] uppercase text-foreground transition-colors">
            <Download className="w-3.5 h-3.5" /> Export
          </button>
        </div>
      </div>

      <div className="mt-4 space-y-4">
        {collisions.map((c) => (
          <div key={c.seat_number} className="border border-sniper bg-obsidian p-3" data-testid={`collision-${c.seat_number}`}>
            <p className="font-mono text-sm text-foreground mb-2">SEAT #{pad3(c.seat_number)} · {c.claims.length} claims</p>
            <div className="overflow-x-auto">
              <table className="w-full text-[11px] font-mono">
                <thead>
                  <tr className="text-muted-foreground uppercase text-left">
                    <th className="py-1 pr-3">Account</th>
                    <th className="py-1 pr-3">Type</th>
                    <th className="py-1 pr-3">ID verified</th>
                    <th className="py-1 pr-3">Seat status</th>
                    <th className="py-1 pr-3">Paid at</th>
                    <th className="py-1 pr-3">Amount</th>
                    <th className="py-1 pr-3">Stripe invoice</th>
                    <th className="py-1 pr-3">Seat claimed</th>
                    <th className="py-1 pr-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {c.claims.map((cl) => (
                    <tr key={cl.seat_doc_id} className="border-t border-sniper/60" style={cl.is_earliest_paid ? { backgroundColor: "#132010" } : undefined} data-testid={`collision-claim-${cl.seat_doc_id}`}>
                      <td className="py-1.5 pr-3 text-foreground">
                        @{cl.username || "?"}
                        {cl.is_earliest_paid && <span className="ml-2 text-[9px] px-1.5 py-0.5 border border-olive text-olive-bright uppercase" data-testid="collision-first-to-pay">First to pay</span>}
                        {cl.account_banned && <span className="ml-2 text-[9px] px-1.5 py-0.5 border border-blaze/60 text-blaze uppercase">banned</span>}
                      </td>
                      <td className="py-1.5 pr-3 text-secondary-foreground">{cl.account_type || "—"}</td>
                      <td className="py-1.5 pr-3" data-testid={`collision-idverified-${cl.seat_doc_id}`}>{cl.identity_verified ? <span className="text-olive-bright">yes</span> : <span className="text-muted-foreground">no</span>}</td>
                      <td className="py-1.5 pr-3 text-secondary-foreground">{cl.seat_status}</td>
                      <td className="py-1.5 pr-3 text-secondary-foreground">{cl.paid_at ? new Date(cl.paid_at).toLocaleString() : "—"}</td>
                      <td className="py-1.5 pr-3 text-secondary-foreground">{money(cl.payment?.amount_cents)}</td>
                      <td className="py-1.5 pr-3 text-secondary-foreground break-all">{cl.payment?.stripe_invoice_id || "—"}</td>
                      <td className="py-1.5 pr-3 text-secondary-foreground">{cl.seat_claimed_at ? new Date(cl.seat_claimed_at).toLocaleDateString() : "—"}</td>
                      <td className="py-1.5 pr-3">
                        {isSuper && (
                          <button
                            data-testid={`collision-release-${cl.seat_doc_id}`}
                            onClick={() => onRelease(cl)}
                            className="border border-blaze/60 hover:bg-blaze/20 text-blaze px-2 py-1 text-[10px] uppercase transition-colors"
                          >
                            Release
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Backfill: review the EXACT recipient list before emailing seatless members. The list renders
// in full here (report-before-send); sending is a separate super-admin action behind a confirm.
function BackfillPanel({ isSuper }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const load = () => api.get("/admin/founding/backfill/recipients").then(({ data }) => setData(data)).catch((e) => toast.error(formatError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);
  const send = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/founding/backfill/send", { confirm: true });
      toast.success(`Backfill: ${r.sent} sent · ${r.skipped} skipped · ${r.failed} failed`);
      setConfirmOpen(false); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const rows = data?.recipients || [];
  const remaining = data?.counter?.public_remaining ?? 0;
  return (
    <div data-testid="founding-backfill">
      <h3 className="font-heading text-lg uppercase tracking-tight mt-10 flex items-center gap-2">
        <Medal className="w-4 h-4 text-[#b08d2c]" /> Backfill — email seatless members
      </h3>
      <Crosshair className="my-4" />
      <div className="border border-sniper bg-surface p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <p className="text-sm font-body text-secondary-foreground max-w-[62ch]">
            Every real, still-eligible account with no seat. Test/demo/internal/banned and
            business-pending accounts are excluded. Review the list, then send. Preview redirects
            every email to the owner (fail-closed guard) — real members are only reached from production.
          </p>
          <button onClick={load} className="p-2 border border-sniper text-muted-foreground hover:text-foreground transition-colors" title="Refresh"><RefreshCw className="w-4 h-4" /></button>
        </div>
        <div className="flex items-center gap-4 flex-wrap mb-3 font-mono text-[11px] uppercase text-muted-foreground">
          <span data-testid="backfill-count">{data?.count ?? "—"} eligible</span>
          <span>· {data?.not_yet_emailed ?? "—"} not yet emailed</span>
          <span>· {data?.already_emailed ?? "—"} already emailed</span>
          <span>· {remaining} public seats open</span>
        </div>
        {rows.length === 0 ? (
          <p className="font-mono text-sm text-muted-foreground py-4" data-testid="backfill-empty">No seatless eligible accounts.</p>
        ) : (
          <div className="overflow-x-auto border border-sniper/60 max-h-[360px] overflow-y-auto">
            <table className="w-full text-[11px] font-mono">
              <thead className="sticky top-0 bg-obsidian">
                <tr className="text-muted-foreground uppercase text-left">
                  <th className="py-1.5 px-3">Handle</th><th className="py-1.5 px-3">Email</th>
                  <th className="py-1.5 px-3">Type</th><th className="py-1.5 px-3">Joined</th><th className="py-1.5 px-3">Emailed</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.user_id} className="border-t border-sniper/50" data-testid={`backfill-recipient-${r.username}`}>
                    <td className="py-1.5 px-3 text-foreground">@{r.username}</td>
                    <td className="py-1.5 px-3 text-secondary-foreground break-all">{r.email}</td>
                    <td className="py-1.5 px-3 text-secondary-foreground">{r.account_type || "—"}</td>
                    <td className="py-1.5 px-3 text-secondary-foreground">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</td>
                    <td className="py-1.5 px-3 text-secondary-foreground">{r.already_emailed_at ? new Date(r.already_emailed_at).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {isSuper ? (
          !confirmOpen ? (
            <button onClick={() => setConfirmOpen(true)} disabled={rows.length === 0 || remaining <= 0}
              data-testid="backfill-send-btn"
              className="mt-4 inline-flex items-center gap-2 bg-[#b08d2c] hover:brightness-110 disabled:opacity-40 text-white font-heading font-bold uppercase text-sm px-5 py-2.5 transition-all">
              Send founding backfill email
            </button>
          ) : (
            <div className="mt-4 border border-[#b08d2c]/60 bg-[#b08d2c]/5 p-4" data-testid="backfill-confirm-box">
              <p className="font-mono text-sm text-foreground">Email {data?.not_yet_emailed ?? 0} member(s) who haven't been emailed yet?</p>
              <div className="flex gap-2 mt-3">
                <button onClick={send} disabled={busy} data-testid="backfill-confirm" className="bg-[#b08d2c] hover:brightness-110 disabled:opacity-50 text-white font-heading font-bold uppercase text-xs px-4 py-2 transition-all">{busy ? "Sending…" : "Confirm & send"}</button>
                <button onClick={() => setConfirmOpen(false)} data-testid="backfill-cancel" className="border border-sniper text-secondary-foreground hover:text-foreground font-mono uppercase text-[11px] px-4 py-2 transition-colors">Cancel</button>
              </div>
            </div>
          )
        ) : (
          <p className="mt-4 font-mono text-xs text-muted-foreground uppercase" data-testid="backfill-locked">Sending requires super admin.</p>
        )}
      </div>
    </div>
  );
}

// Reconcile users.founding_number with the authoritative seat ledger so the gold Founding badge
// renders wherever a seat exists but the field drifted. Dry-run first (report), then apply.
function ReconcilePanel({ isSuper, onDone }) {
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState("");
  const [clearOrphans, setClearOrphans] = useState(false);

  const run = async (commit) => {
    setBusy(commit ? "commit" : "dry");
    try {
      const { data } = await api.post(`/admin/founding/reconcile?commit=${commit}&clear_orphans=${commit && clearOrphans}`, {});
      setReport(data);
      if (commit) {
        const s = data.summary;
        toast.success(`Reconciled — fixed ${s.fixed}, restored ${s.set_missing}${clearOrphans ? `, cleared ${s.orphans} orphans` : ""}.`);
        onDone && onDone();
      }
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  if (!isSuper) return null;
  const s = report?.summary;
  const willChange = s ? (s.fixed + s.set_missing + (clearOrphans ? s.orphans : 0)) : 0;

  return (
    <div data-testid="founding-reconcile">
      <h3 className="font-heading text-lg uppercase tracking-tight mt-10 flex items-center gap-2">
        <Wrench className="w-4 h-4 text-[#b08d2c]" /> Reconcile founding numbers
      </h3>
      <Crosshair className="my-4" />
      <div className="border border-sniper bg-surface p-5">
        <p className="text-sm font-body text-secondary-foreground max-w-[70ch]">
          The gold Founding badge reads each member's <span className="font-mono text-foreground">founding_number</span>, not the seat grid.
          If a seat exists but that field drifted (a legacy assign/move), the badge disappears everywhere while the grid still shows the seat.
          Run a dry-run to see exactly what would change, then apply. Nothing is written until you click Apply.
        </p>
        <div className="flex items-center gap-2 mt-4 flex-wrap">
          <button onClick={() => run(false)} disabled={busy !== ""} data-testid="reconcile-dryrun"
            className="inline-flex items-center gap-2 border border-sniper hover:border-foreground disabled:opacity-40 px-4 py-2 font-mono text-[11px] uppercase text-foreground transition-colors">
            {busy === "dry" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Run dry-run
          </button>
          {report && (
            <button onClick={() => run(true)} disabled={busy !== "" || willChange === 0} data-testid="reconcile-apply"
              className="inline-flex items-center gap-2 bg-[#b08d2c] hover:brightness-110 disabled:opacity-40 text-white font-heading font-bold uppercase text-sm px-5 py-2 transition-all">
              {busy === "commit" ? "Applying…" : `Apply ${willChange} fix${willChange === 1 ? "" : "es"}`}
            </button>
          )}
        </div>

        {report && (
          <div className="mt-4 space-y-3" data-testid="reconcile-report">
            <div className="flex flex-wrap gap-4 font-mono text-[11px] uppercase">
              <span className="text-olive-bright" data-testid="reconcile-set">{s.set_missing} restore</span>
              <span className="text-olive-bright">{s.fixed} fix</span>
              <span className="text-muted-foreground">{s.already_ok} already ok</span>
              <span className="text-[#E8C46A]">{s.anomalies} anomalies</span>
              <span className="text-secondary-foreground">{s.orphans} orphans</span>
            </div>

            {(report.set_missing.length > 0 || report.fixed.length > 0) && (
              <div className="border border-[#4e8a1f]/40 bg-[#4e8a1f]/5 p-3">
                <p className="font-mono text-[10px] uppercase text-olive-bright mb-1.5">Will restore / fix the badge for</p>
                {[...report.set_missing, ...report.fixed].map((e) => (
                  <div key={e.user_id} className="font-mono text-[11px] text-foreground" data-testid={`reconcile-fix-${e.username}`}>
                    @{e.username}: founding_number {e.from == null ? "(none)" : `#${pad3(e.from)}`} → #{pad3(e.to)}
                  </div>
                ))}
              </div>
            )}

            {report.anomalies.length > 0 && (
              <div className="border border-[#b08d2c]/40 bg-[#b08d2c]/5 p-3">
                <p className="font-mono text-[10px] uppercase text-[#E8C46A] mb-1.5 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Manual review — multiple/stale seats</p>
                {report.anomalies.map((a, i) => (
                  <div key={i} className="font-mono text-[11px] text-secondary-foreground" data-testid={`reconcile-anomaly-${i}`}>
                    @{a.username || a.user_id}: {a.issue}{a.seats ? ` [${a.seats.map(pad3).join(", ")}]` : ""}{a.keeping ? ` — keeping #${pad3(a.keeping)}` : ""}
                  </div>
                ))}
              </div>
            )}

            {report.orphans.length > 0 && (
              <div className="border border-sniper p-3">
                <p className="font-mono text-[10px] uppercase text-muted-foreground mb-1.5">Orphans — a founding number with no active seat (often demo/showcase fixtures)</p>
                <div className="font-mono text-[11px] text-secondary-foreground">
                  {report.orphans.map((o) => `@${o.username} #${pad3(o.founding_number)}`).join(" · ")}
                </div>
                <label className="flex items-center gap-2 mt-2 cursor-pointer">
                  <input type="checkbox" checked={clearOrphans} onChange={(e) => setClearOrphans(e.target.checked)} className="accent-blaze w-3.5 h-3.5" data-testid="reconcile-clear-orphans" />
                  <span className="font-mono text-[10px] uppercase text-blaze">Also clear these on Apply (removes the badge — only if they truly hold no seat)</span>
                </label>
              </div>
            )}

            {willChange === 0 && report.orphans.length === 0 && (
              <p className="font-mono text-[11px] text-olive-bright flex items-center gap-1.5" data-testid="reconcile-clean"><CheckCircle2 className="w-3.5 h-3.5" /> Everything already in sync — nothing to fix.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminFounding() {
  const { user } = useAuth();
  const isSuper = user?.role === "admin" || user?.role === "super_admin"; // admin == super privileges
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ seat_number: "", username: "", reason: "" });
  const [holdForm, setHoldForm] = useState({ seat_number: "", username: "", reason: "" });
  const [moveForm, setMoveForm] = useState({ username: "", to_seat: "", reason: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/founding/seat-map").then(({ data }) => setData(data)).catch((e) => toast.error(formatError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const seats = data?.seats || [];
  const counts = data?.counts || {};
  const collisions = data?.collisions || [];
  const reservedMax = data?.reserved_max || 10;
  const reservedOpen = seats.filter((s) => s.seat_number <= reservedMax && s.status === "reserved");

  const release = async (claim) => {
    const reason = window.prompt(
      `Release seat #${pad3(claim.seat_number || 0)} held by @${claim.username || "?"}?\n\n` +
      `This DELETES that seat document and clears the holder's founding number. The other claim keeps the number.\n\n` +
      `Enter a reason (min 10 chars) for the audit log:`
    );
    if (reason == null) return;
    if (reason.trim().length < 10) { toast.error("A reason of at least 10 characters is required."); return; }
    setBusy(true);
    try {
      await api.post("/admin/founding/release", { seat_doc_id: claim.seat_doc_id, reason: reason.trim() });
      toast.success(`Released @${claim.username || "claim"} from seat #${pad3(claim.seat_number || 0)}.`);
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const assign = async (e) => {
    e.preventDefault();
    if (form.reason.trim().length < 10) { toast.error("A reason (≥10 characters) is required."); return; }
    if (!form.seat_number || !form.username.trim()) { toast.error("Pick a reserved seat and a username."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/founding/assign", {
        seat_number: Number(form.seat_number),
        username: form.username.trim().replace(/^@/, ""), reason: form.reason.trim(),
      });
      toast.success(data?.from_seat
        ? `Assigned reserved seat #${pad3(form.seat_number)} — vacated #${pad3(data.from_seat)}.`
        : `Assigned reserved seat #${pad3(form.seat_number)}.`);
      setForm({ seat_number: "", username: "", reason: "" });
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const fieldCls = "mt-1 w-full bg-input border border-sniper focus:border-[#b08d2c] outline-none px-3 py-2 text-foreground text-sm";
  const availablePublic = seats.filter((s) => s.status === "available" && s.seat_number >= (data?.public_min || 11));
  const heldSeats = seats.filter((s) => s.status === "held");
  const holdWarnDays = data?.hold_warn_days || 3;
  const expiringSoon = heldSeats.filter((s) => { const d = daysUntil(s.hold_expires_at); return d != null && d <= holdWarnDays; });

  const holdSeat = async (e) => {
    e.preventDefault();
    if (holdForm.reason.trim().length < 10) { toast.error("A reason (≥10 characters) is required."); return; }
    if (!holdForm.seat_number || !holdForm.username.trim()) { toast.error("Pick a seat and a member."); return; }
    setBusy(true);
    try {
      await api.post("/admin/founding/hold", { seat_number: Number(holdForm.seat_number), username: holdForm.username.trim().replace(/^@/, ""), reason: holdForm.reason.trim() });
      toast.success(`Holding seat #${pad3(holdForm.seat_number)} for @${holdForm.username.replace(/^@/, "")}.`);
      setHoldForm({ seat_number: "", username: "", reason: "" }); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const moveSeat = async (e) => {
    e.preventDefault();
    if (moveForm.reason.trim().length < 10) { toast.error("A reason (≥10 characters) is required."); return; }
    if (!moveForm.to_seat || !moveForm.username.trim()) { toast.error("Pick a member and a target seat."); return; }
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/founding/move", { username: moveForm.username.trim().replace(/^@/, ""), to_seat: Number(moveForm.to_seat), reason: moveForm.reason.trim() });
      toast.success(`Moved @${r.username} from #${pad3(r.from_seat)} to #${pad3(r.to_seat)}.`);
      setMoveForm({ username: "", to_seat: "", reason: "" }); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const releaseHold = async (s) => {
    const reason = window.prompt(`Cancel the hold on seat #${pad3(s.seat_number)} (held for @${s.held_for_username || s.username || "?"})?\n\nEnter a reason (min 10 chars):`);
    if (reason == null) return;
    if (reason.trim().length < 10) { toast.error("A reason of at least 10 characters is required."); return; }
    setBusy(true);
    try { await api.post("/admin/founding/release", { seat_doc_id: `seat:${s.seat_number}`, reason: reason.trim() }); toast.success(`Hold on #${pad3(s.seat_number)} released.`); load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="text-foreground" data-testid="admin-founding">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-4xl font-bold uppercase tracking-tight flex items-center gap-2">
          <Medal className="w-7 h-7 text-[#b08d2c]" /> Founding Seats
        </h2>
        <button onClick={load} className="p-2 border border-sniper text-muted-foreground hover:text-foreground transition-colors" title="Refresh"><RefreshCw className="w-4 h-4" /></button>
      </div>
      <p className="text-sm font-body text-secondary-foreground mt-2">
        ONE global pool of {data?.total_seats || 100}, shared by individuals and businesses. Seats 1–{reservedMax} are reserved
        (super-admin assign only); {data?.public_min || 11}–{data?.total_seats || 100} are public and member-claimable. Retired
        seats are never reissued. Chip colour follows the holder's current account type, not the seat.
      </p>
      <Crosshair className="my-5" />

      {collisions.length > 0 && (
        <CollisionBanner collisions={collisions} isSuper={isSuper} onRelease={release} />
      )}

      {/* legend */}
      <div className="flex flex-wrap gap-3 mb-5" data-testid="founding-legend">
        {LEGEND.map(([k, label]) => (
          <span key={k} className="flex items-center gap-1.5 font-mono text-[10px] uppercase text-muted-foreground">
            <span className={`w-4 h-4 border ${STATUS_CLS[k]}`} /> {label}
          </span>
        ))}
      </div>

      {!data ? (
        <p className="font-mono text-sm text-muted-foreground py-8">Loading…</p>
      ) : (
        <div className="border border-sniper bg-surface p-4" data-testid="seat-board">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-lg uppercase tracking-tight">Founding pool · {data.total_seats || 100} seats</h3>
            <span className="font-mono text-[11px] uppercase text-muted-foreground" data-testid="seat-board-count">
              {counts.public_remaining} / {counts.public_seats} open · {counts.reserved_assigned || 0} reserved assigned · {counts.retired || 0} retired
            </span>
          </div>
          <div className="grid grid-cols-10 gap-1">
            {seats.map((s) => <SeatCell key={s.seat_number} s={s} />)}
          </div>
        </div>
      )}

      {/* reserved assignment (super admin only) */}
      <h3 className="font-heading text-lg uppercase tracking-tight mt-10 flex items-center gap-2">
        <Lock className="w-4 h-4 text-[#b08d2c]" /> Assign a reserved seat (1–{reservedMax})
      </h3>
      <Crosshair className="my-4" />
      {isSuper ? (
        <form onSubmit={assign} className="border border-sniper bg-surface p-5 grid sm:grid-cols-2 gap-4 max-w-2xl" data-testid="reserved-assign-form">
          <div>
            <label className="font-mono text-[11px] uppercase text-secondary-foreground">Reserved seat</label>
            <select
              data-testid="assign-seat"
              value={form.seat_number}
              onChange={(e) => setForm({ ...form, seat_number: e.target.value })}
              className="mt-1 w-full bg-input border border-sniper focus:border-[#b08d2c] outline-none px-3 py-2 text-foreground text-sm"
            >
              <option value="">Select…</option>
              {reservedOpen.map((s) => (
                <option key={s.seat_number} value={s.seat_number}>#{pad3(s.seat_number)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="font-mono text-[11px] uppercase text-secondary-foreground">Assign to username</label>
            <input
              data-testid="assign-username"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              placeholder="username (no @)"
              className="mt-1 w-full bg-input border border-sniper focus:border-[#b08d2c] outline-none px-3 py-2 text-foreground text-sm"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="font-mono text-[11px] uppercase text-secondary-foreground">Reason (≥10 chars — goes to the audit log)</label>
            <textarea
              data-testid="assign-reason"
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              rows={2}
              placeholder="Why is this reserved seat being assigned?"
              className="mt-1 w-full bg-input border border-sniper focus:border-[#b08d2c] outline-none px-3 py-2 text-foreground text-sm resize-none"
            />
          </div>
          <button
            data-testid="assign-submit"
            disabled={busy}
            className="sm:col-span-2 inline-flex items-center justify-center gap-2 bg-[#b08d2c] hover:brightness-110 disabled:opacity-50 text-white font-heading font-bold uppercase text-sm px-5 py-2.5 transition-all"
          >
            {busy ? "Assigning…" : "Assign reserved seat"}
          </button>
        </form>
      ) : (
        <div className="border border-sniper bg-surface p-5 text-sm font-mono text-muted-foreground" data-testid="reserved-assign-locked">
          Reserved-seat assignment requires super admin.
        </div>
      )}

      {/* hold + move a public seat (super admin only) */}
      <h3 className="font-heading text-lg uppercase tracking-tight mt-10 flex items-center gap-2">
        <Clock className="w-4 h-4 text-[#b08d2c]" /> Reserve a public seat for a member
      </h3>
      <Crosshair className="my-4" />

      {expiringSoon.length > 0 && (
        <div className="border border-[#b08d2c]/70 bg-[#b08d2c]/10 p-3 mb-4 flex items-start gap-2" data-testid="hold-expiry-warning">
          <AlertTriangle className="w-4 h-4 text-[#E8C46A] mt-0.5 shrink-0" />
          <p className="text-sm font-body text-secondary-foreground">
            {expiringSoon.length} seat hold{expiringSoon.length === 1 ? "" : "s"} expiring within {holdWarnDays} days:{" "}
            {expiringSoon.map((s) => `#${pad3(s.seat_number)} (@${s.held_for_username || s.username})`).join(", ")}. They return to the pool automatically.
          </p>
        </div>
      )}

      {isSuper ? (
        <div className="grid lg:grid-cols-2 gap-5">
          <form onSubmit={holdSeat} className="border border-sniper bg-surface p-5 space-y-3" data-testid="hold-seat-form">
            <p className="font-heading uppercase tracking-tight text-foreground flex items-center gap-2"><Clock className="w-4 h-4 text-[#b08d2c]" /> Hold a seat</p>
            <p className="text-[11px] font-mono text-muted-foreground">Reserve an unclaimed public seat for a named member. Auto-expires in {data?.hold_days || 14} days and returns to the pool.</p>
            <div>
              <label className="font-mono text-[11px] uppercase text-secondary-foreground">Seat</label>
              <select data-testid="hold-seat-select" value={holdForm.seat_number} onChange={(e) => setHoldForm({ ...holdForm, seat_number: e.target.value })} className={fieldCls}>
                <option value="">Select an open seat…</option>
                {availablePublic.map((s) => <option key={s.seat_number} value={s.seat_number}>#{pad3(s.seat_number)}</option>)}
              </select>
            </div>
            <div>
              <label className="font-mono text-[11px] uppercase text-secondary-foreground">Hold for (username)</label>
              <input data-testid="hold-username" value={holdForm.username} onChange={(e) => setHoldForm({ ...holdForm, username: e.target.value })} placeholder="username (no @)" className={fieldCls} />
            </div>
            <div>
              <label className="font-mono text-[11px] uppercase text-secondary-foreground">Reason (≥10 chars — audited)</label>
              <textarea data-testid="hold-reason" rows={2} value={holdForm.reason} onChange={(e) => setHoldForm({ ...holdForm, reason: e.target.value })} className={`${fieldCls} resize-none`} />
            </div>
            <button data-testid="hold-submit" disabled={busy} className="w-full bg-[#b08d2c] hover:brightness-110 disabled:opacity-50 text-white font-heading font-bold uppercase text-sm py-2.5 transition-all">{busy ? "Holding…" : "Hold seat"}</button>
          </form>

          <form onSubmit={moveSeat} className="border border-sniper bg-surface p-5 space-y-3" data-testid="move-seat-form">
            <p className="font-heading uppercase tracking-tight text-foreground flex items-center gap-2"><ArrowRightLeft className="w-4 h-4 text-[#b08d2c]" /> Move a seat</p>
            <p className="text-[11px] font-mono text-muted-foreground">Change a member's confirmed seat to a different unclaimed public seat. Atomic — they never hold zero or two; the old number returns to the pool.</p>
            <div>
              <label className="font-mono text-[11px] uppercase text-secondary-foreground">Member (username)</label>
              <input data-testid="move-username" value={moveForm.username} onChange={(e) => setMoveForm({ ...moveForm, username: e.target.value })} placeholder="username (no @)" className={fieldCls} />
            </div>
            <div>
              <label className="font-mono text-[11px] uppercase text-secondary-foreground">New seat</label>
              <select data-testid="move-seat-select" value={moveForm.to_seat} onChange={(e) => setMoveForm({ ...moveForm, to_seat: e.target.value })} className={fieldCls}>
                <option value="">Select an open seat…</option>
                {availablePublic.map((s) => <option key={s.seat_number} value={s.seat_number}>#{pad3(s.seat_number)}</option>)}
              </select>
            </div>
            <div>
              <label className="font-mono text-[11px] uppercase text-secondary-foreground">Reason (≥10 chars — audited)</label>
              <textarea data-testid="move-reason" rows={2} value={moveForm.reason} onChange={(e) => setMoveForm({ ...moveForm, reason: e.target.value })} className={`${fieldCls} resize-none`} />
            </div>
            <button data-testid="move-submit" disabled={busy} className="w-full bg-[#b08d2c] hover:brightness-110 disabled:opacity-50 text-white font-heading font-bold uppercase text-sm py-2.5 transition-all">{busy ? "Moving…" : "Move seat"}</button>
          </form>
        </div>
      ) : (
        <div className="border border-sniper bg-surface p-5 text-sm font-mono text-muted-foreground" data-testid="hold-move-locked">Holding and moving seats requires super admin.</div>
      )}

      {isSuper && heldSeats.length > 0 && (
        <div className="mt-5 border border-sniper bg-surface p-4" data-testid="held-seats-list">
          <p className="font-mono text-[11px] uppercase text-muted-foreground mb-2">Current holds</p>
          <div className="space-y-1.5">
            {heldSeats.map((s) => {
              const d = daysUntil(s.hold_expires_at);
              return (
                <div key={s.seat_number} className="flex items-center justify-between gap-3 text-xs font-mono border-b border-sniper/40 pb-1.5" data-testid={`held-row-${s.seat_number}`}>
                  <span className="text-foreground">#{pad3(s.seat_number)} · held for @{s.held_for_username || s.username || "?"}</span>
                  <span className="text-secondary-foreground">{d != null ? `${d}d left` : "—"}</span>
                  <button data-testid={`held-release-${s.seat_number}`} onClick={() => releaseHold(s)} className="border border-blaze/60 hover:bg-blaze/20 text-blaze px-2 py-0.5 text-[10px] uppercase transition-colors">Release</button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <BackfillPanel isSuper={isSuper} />
      <ReconcilePanel isSuper={isSuper} onDone={load} />
    </div>
  );
}
